import { useState, useEffect, useCallback } from 'react';
import { 
  AIRecommendation, 
  AIPerformanceReport, 
  AIStudyPlan,
  AILearningPath,
  AIFeedback
} from '../types';
import { aiService } from '../utils/aiService';

// Local storage keys
const getStorageKey = (userId: string, key: string) => `ai_learning_${userId}_${key}`;

// Helper function to serialize data for localStorage (converts Date objects to strings)
const serializeForStorage = (data: any): string => {
  return JSON.stringify(data, (key, value) => {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  });
};

// Helper function to deserialize data from localStorage (converts date strings back to Date objects)
const deserializeFromStorage = (data: any): any => {
  return JSON.parse(data, (key, value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      return new Date(value);
    }
    return value;
  });
};

export const useAILearning = (userId: string) => {
  // Initialize state from local storage
  const [recommendations, setRecommendations] = useState<AIRecommendation[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(getStorageKey(userId, 'recommendations'));
      return stored ? deserializeFromStorage(stored) : [];
    } catch (error) {
      console.warn('Failed to load recommendations from localStorage:', error);
      return [];
    }
  });

  const [performanceReports, setPerformanceReports] = useState<AIPerformanceReport[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(getStorageKey(userId, 'performanceReports'));
      return stored ? deserializeFromStorage(stored) : [];
    } catch (error) {
      console.warn('Failed to load performance reports from localStorage:', error);
      return [];
    }
  });

  const [studyPlans, setStudyPlans] = useState<AIStudyPlan[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(getStorageKey(userId, 'studyPlans'));
      return stored ? deserializeFromStorage(stored) : [];
    } catch (error) {
      console.warn('Failed to load study plans from localStorage:', error);
      return [];
    }
  });

  const [learningPaths, setLearningPaths] = useState<AILearningPath[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(getStorageKey(userId, 'learningPaths'));
      return stored ? deserializeFromStorage(stored) : [];
    } catch (error) {
      console.warn('Failed to load learning paths from localStorage:', error);
      return [];
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Save recommendations to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined' && userId) {
      try {
        localStorage.setItem(getStorageKey(userId, 'recommendations'), serializeForStorage(recommendations));
      } catch (error) {
        console.warn('Failed to save recommendations to localStorage:', error);
      }
    }
  }, [recommendations, userId]);

  // Save performance reports to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined' && userId) {
      try {
        localStorage.setItem(getStorageKey(userId, 'performanceReports'), serializeForStorage(performanceReports));
      } catch (error) {
        console.warn('Failed to save performance reports to localStorage:', error);
      }
    }
  }, [performanceReports, userId]);

  // Save study plans to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined' && userId) {
      try {
        localStorage.setItem(getStorageKey(userId, 'studyPlans'), serializeForStorage(studyPlans));
      } catch (error) {
        console.warn('Failed to save study plans to localStorage:', error);
      }
    }
  }, [studyPlans, userId]);

  // Save learning paths to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined' && userId) {
      try {
        localStorage.setItem(getStorageKey(userId, 'learningPaths'), serializeForStorage(learningPaths));
      } catch (error) {
        console.warn('Failed to save learning paths to localStorage:', error);
      }
    }
  }, [learningPaths, userId]);

  // Generate AI recommendations
  const generateRecommendations = useCallback(async (userPerformance: any) => {
    try {
      setLoading(true);
      setError(null);
      const recs = await aiService.generateRecommendations(userId, userPerformance);
      setRecommendations(recs);
      return recs;
    } catch (err) {
      setError('Failed to generate recommendations');
      console.error('Error generating recommendations:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Generate performance report
  const generatePerformanceReport = useCallback(async (
    courseId: string, 
    period: 'weekly' | 'monthly' | 'quarterly'
  ) => {
    try {
      setLoading(true);
      setError(null);
      const report = await aiService.generatePerformanceReport(userId, courseId, period);
      setPerformanceReports(prev => [report, ...prev]);
      return report;
    } catch (err) {
      setError('Failed to generate performance report');
      console.error('Error generating performance report:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Generate study plan
  const generateStudyPlan = useCallback(async (
    courseId: string, 
    userSchedule: any, 
    learningGoals: string[]
  ) => {
    try {
      setLoading(true);
      setError(null);
      const plan = await aiService.generateStudyPlan(userId, courseId, userSchedule, learningGoals);
      setStudyPlans(prev => [plan, ...prev]);
      return plan;
    } catch (err) {
      setError('Failed to generate study plan');
      console.error('Error generating study plan:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Mark recommendation as read
  const markRecommendationAsRead = useCallback((recommendationId: string) => {
    setRecommendations(prev => 
      prev.map(rec => 
        rec.id === recommendationId ? { ...rec, isRead: true } : rec
      )
    );
  }, []);

  // Update study plan
  const updateStudyPlan = useCallback((planId: string, updates: Partial<AIStudyPlan>) => {
    setStudyPlans(prev => 
      prev.map(plan => 
        plan.id === planId ? { ...plan, ...updates, updatedAt: new Date() } : plan
      )
    );
  }, []);

  // Get active study plan
  const getActiveStudyPlan = useCallback(() => {
    return studyPlans.find(plan => plan.isActive);
  }, [studyPlans]);

  // Get latest performance report
  const getLatestPerformanceReport = useCallback(() => {
    return performanceReports[0] || null;
  }, [performanceReports]);

  // Generate content (quiz, summary, flashcards, practice exercises)
  const generateContent = useCallback(async (
    type: 'quiz' | 'summary' | 'flashcards' | 'practice_exercises',
    courseId: string = 'course_1',
    lessonId: string = 'lesson_1',
    lessonContent: string = 'Sample lesson content for AI generation'
  ) => {
    try {
      setLoading(true);
      setError(null);
      
      const content = await aiService.generateContent(courseId, lessonId, type, lessonContent);
      
      return content;
    } catch (err) {
      const errorMsg = `Failed to generate ${type} content`;
      setError(errorMsg);
      console.error(`Error generating ${type} content:`, err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get unread recommendations count
  const getUnreadRecommendationsCount = useCallback(() => {
    return recommendations.filter(rec => !rec.isRead).length;
  }, [recommendations]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear all data from localStorage
  const clearAllData = useCallback(() => {
    setRecommendations([]);
    setPerformanceReports([]);
    setStudyPlans([]);
    setLearningPaths([]);
    if (typeof window !== 'undefined' && userId) {
      try {
        localStorage.removeItem(getStorageKey(userId, 'recommendations'));
        localStorage.removeItem(getStorageKey(userId, 'performanceReports'));
        localStorage.removeItem(getStorageKey(userId, 'studyPlans'));
        localStorage.removeItem(getStorageKey(userId, 'learningPaths'));
      } catch (error) {
        console.warn('Failed to clear data from localStorage:', error);
      }
    }
  }, [userId]);

  return {
    // State
    recommendations,
    performanceReports,
    studyPlans,
    learningPaths,
    loading,
    error,
    
    // Actions
    generateRecommendations,
    generatePerformanceReport,
    generateStudyPlan,
    generateContent,
    markRecommendationAsRead,
    updateStudyPlan,
    clearAllData,
    
    // Getters
    getActiveStudyPlan,
    getLatestPerformanceReport,
    getUnreadRecommendationsCount,
    clearError
  };
};
